import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'sonner'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../context/LanguageContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'
import { 
  ArrowLeft, TrendingUp, TrendingDown, Wallet, Calendar, 
  DollarSign, PiggyBank, Loader2, HelpCircle, Info,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react'
import { cn } from '../lib/utils'
import { CurrencyInput, formatCurrencyDisplay } from '../components/CurrencyInput'

const API = process.env.REACT_APP_BACKEND_URL

export default function InvestmentProfitPage() {
  const { token } = useAuth()
  const { locale } = useLanguage()
  const lang = locale.startsWith('es') ? 'es' : 'en'
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [accounts, setAccounts] = useState([])
  const [editingLog, setEditingLog] = useState(null)
  
  // Form state
  const [formData, setFormData] = useState({
    cuenta_id: '',
    periodo_mes: new Date().toISOString().slice(0, 7), // YYYY-MM
    valor_cierre: '',
    aportaciones: '',
    retiros: '',
    nota: ''
  })
  
  // Previous closing data
  const [prevClosing, setPrevClosing] = useState(null)
  const [loadingPrevClosing, setLoadingPrevClosing] = useState(false)
  
  // Fetch accounts on mount
  useEffect(() => {
    fetchAccounts()
  }, [token])
  
  const fetchAccounts = async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${API}/api/investments/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAccounts(res.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
      toast.error(lang === 'es' ? 'Error al cargar cuentas' : 'Error loading accounts')
    } finally {
      setLoading(false)
    }
  }
  
  // Fetch previous closing when account or period changes
  const fetchPrevClosing = useCallback(async () => {
    if (!formData.cuenta_id || !formData.periodo_mes) {
      setPrevClosing(null)
      return
    }
    
    try {
      setLoadingPrevClosing(true)
      const res = await axios.get(
        `${API}/api/investments/profit-logs/by-account-period/${formData.cuenta_id}/${formData.periodo_mes}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      if (res.data) {
        if (res.data.id) {
          // Existing log found - load for editing
          setEditingLog(res.data)
          setFormData(prev => ({
            ...prev,
            valor_cierre: String(res.data.valor_cierre || ''),
            aportaciones: String(res.data.aportaciones || ''),
            retiros: String(res.data.retiros || ''),
            nota: res.data.nota || ''
          }))
          setPrevClosing({
            valor: res.data.valor_cierre_anterior,
            periodo: res.data.periodo_mes_anterior
          })
        } else {
          // No existing log - just set previous closing info
          setEditingLog(null)
          setPrevClosing({
            valor: res.data.valor_cierre_anterior,
            periodo: res.data.periodo_mes_anterior
          })
          // Reset form fields except account and period
          setFormData(prev => ({
            ...prev,
            valor_cierre: '',
            aportaciones: '',
            retiros: '',
            nota: ''
          }))
        }
      }
    } catch (error) {
      console.error('Error fetching previous closing:', error)
      setPrevClosing(null)
    } finally {
      setLoadingPrevClosing(false)
    }
  }, [formData.cuenta_id, formData.periodo_mes, token])
  
  useEffect(() => {
    fetchPrevClosing()
  }, [fetchPrevClosing])
  
  // Calculate preview values
  const calculatePreview = () => {
    const valorCierre = parseFloat(formData.valor_cierre) || 0
    const aportaciones = parseFloat(formData.aportaciones) || 0
    const retiros = parseFloat(formData.retiros) || 0
    const valorCierreAnterior = prevClosing?.valor
    
    if (!valorCierreAnterior || valorCierreAnterior <= 0) {
      return { ganancia: null, porcentaje: null, isFirst: true }
    }
    
    const ganancia = valorCierre - valorCierreAnterior - aportaciones + retiros
    const porcentaje = (ganancia / valorCierreAnterior) * 100
    
    return { ganancia, porcentaje, isFirst: false }
  }
  
  const preview = calculatePreview()
  const selectedAccount = accounts.find(a => a.id === formData.cuenta_id)
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.cuenta_id) {
      toast.error(lang === 'es' ? 'Selecciona una cuenta' : 'Select an account')
      return
    }
    
    if (!formData.valor_cierre || parseFloat(formData.valor_cierre) <= 0) {
      toast.error(lang === 'es' ? 'Ingresa el valor de cierre' : 'Enter closing value')
      return
    }
    
    try {
      setSaving(true)
      
      const payload = {
        cuenta_id: formData.cuenta_id,
        periodo_mes: formData.periodo_mes,
        valor_cierre: parseFloat(formData.valor_cierre),
        aportaciones: parseFloat(formData.aportaciones) || 0,
        retiros: parseFloat(formData.retiros) || 0,
        nota: formData.nota || null
      }
      
      if (editingLog) {
        // Update existing
        await axios.put(
          `${API}/api/investments/profit-logs/${editingLog.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        toast.success(lang === 'es' ? 'Registro actualizado' : 'Record updated')
      } else {
        // Create new
        await axios.post(
          `${API}/api/investments/profit-logs`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        toast.success(lang === 'es' ? 'Registro guardado' : 'Record saved')
      }
      
      navigate('/inversiones')
    } catch (error) {
      console.error('Error saving:', error)
      if (error.response?.status === 409) {
        toast.error(lang === 'es' ? 'Ya existe un registro para este mes' : 'Record already exists for this month')
      } else {
        toast.error(lang === 'es' ? 'Error al guardar' : 'Error saving')
      }
    } finally {
      setSaving(false)
    }
  }
  
  const formatCurrency = (value, currency = 'MXN') => {
    return formatCurrencyDisplay(value, currency)
  }
  
  const formatCurrencyWithSign = (value, currency = 'MXN') => {
    return formatCurrencyDisplay(value, currency, true)
  }
  
  const getMonthName = (periodo) => {
    if (!periodo) return ''
    const [year, month] = periodo.split('-')
    const date = new Date(parseInt(year), parseInt(month) - 1)
    return date.toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { month: 'long', year: 'numeric' })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Sticky Header with back button */}
      <div className="sticky top-0 z-50 bg-background border-b p-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/inversiones')}
            className="rounded-full h-10 w-10 flex-shrink-0"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">
              {lang === 'es' ? 'Cierre Mensual' : 'Monthly Closing'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {lang === 'es' ? 'Registra el valor de cierre de tu cuenta' : 'Record your account closing value'}
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-4">
      {/* Help Info */}
      <Card className="mb-4 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
        <CardContent className="p-3 flex items-start gap-3">
          <HelpCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {lang === 'es' 
              ? 'El rendimiento se calcula automáticamente usando el cierre del mes anterior como base.'
              : 'Profit is calculated automatically using the previous month closing as baseline.'}
          </p>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Account Selection */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                {lang === 'es' ? 'Cuenta' : 'Account'} *
              </Label>
              <Select
                value={formData.cuenta_id}
                onValueChange={(val) => setFormData(prev => ({ ...prev, cuenta_id: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={lang === 'es' ? 'Selecciona una cuenta' : 'Select an account'} />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.nombre} ({acc.moneda})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {lang === 'es' ? 'Periodo (Mes)' : 'Period (Month)'} *
              </Label>
              <Input
                type="month"
                value={formData.periodo_mes}
                onChange={(e) => setFormData(prev => ({ ...prev, periodo_mes: e.target.value }))}
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Previous Closing Info */}
        {formData.cuenta_id && (
          <Card className={cn(
            "transition-all",
            loadingPrevClosing && "opacity-50"
          )}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Info className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">
                  {lang === 'es' ? 'Información del mes anterior' : 'Previous month info'}
                </span>
              </div>
              
              {loadingPrevClosing ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">{lang === 'es' ? 'Cargando...' : 'Loading...'}</span>
                </div>
              ) : prevClosing?.valor ? (
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      {lang === 'es' ? 'Cierre anterior' : 'Previous closing'}
                    </span>
                    <span className="font-bold">
                      {formatCurrency(prevClosing.valor, selectedAccount?.moneda)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {lang === 'es' 
                      ? 'Este es el primer registro para esta cuenta. Aún no se puede calcular rendimiento.'
                      : 'This is the first record for this account. Profit cannot be calculated yet.'}
                  </p>
                </div>
              )}
              
              {editingLog && (
                <div className="mt-2 px-2 py-1 bg-primary/10 rounded text-xs text-primary font-medium">
                  {lang === 'es' ? '✏️ Editando registro existente' : '✏️ Editing existing record'}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Closing Value Input */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                {lang === 'es' ? 'Valor de cierre del mes' : 'Monthly closing value'} *
              </Label>
              <CurrencyInput
                value={formData.valor_cierre}
                onChange={(val) => setFormData(prev => ({ ...prev, valor_cierre: val }))}
                currency={selectedAccount?.moneda || 'MXN'}
                placeholder={lang === 'es' ? 'Valor actual de la cuenta' : 'Current account value'}
                className="h-12 text-lg"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm">
                  <PiggyBank className="w-4 h-4 text-blue-500" />
                  {lang === 'es' ? 'Aportaciones' : 'Contributions'}
                </Label>
                <CurrencyInput
                  value={formData.aportaciones}
                  onChange={(val) => setFormData(prev => ({ ...prev, aportaciones: val }))}
                  currency={selectedAccount?.moneda || 'MXN'}
                  placeholder="0.00"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm">
                  <ArrowDownRight className="w-4 h-4 text-rose-500" />
                  {lang === 'es' ? 'Retiros' : 'Withdrawals'}
                </Label>
                <CurrencyInput
                  value={formData.retiros}
                  onChange={(val) => setFormData(prev => ({ ...prev, retiros: val }))}
                  currency={selectedAccount?.moneda || 'MXN'}
                  placeholder="0.00"
                  className="h-11"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">
                {lang === 'es' ? 'Nota (opcional)' : 'Note (optional)'}
              </Label>
              <Textarea
                value={formData.nota}
                onChange={(e) => setFormData(prev => ({ ...prev, nota: e.target.value }))}
                placeholder={lang === 'es' ? 'Comentarios adicionales...' : 'Additional comments...'}
                className="h-20 resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Preview Card */}
        {formData.valor_cierre && parseFloat(formData.valor_cierre) > 0 && (
          <Card className="border-2 border-primary/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                {preview.ganancia !== null ? (
                  preview.ganancia >= 0 ? (
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-rose-500" />
                  )
                ) : (
                  <Minus className="w-5 h-5 text-muted-foreground" />
                )}
                {lang === 'es' ? 'Vista previa del registro' : 'Record Preview'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 gap-2 text-sm">
                {prevClosing?.valor && (
                  <div className="flex justify-between items-center py-1 border-b border-dashed">
                    <span className="text-muted-foreground">
                      {lang === 'es' ? 'Cierre anterior:' : 'Prev. closing:'}
                    </span>
                    <span className="font-mono">{formatCurrency(prevClosing.valor, selectedAccount?.moneda)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-1 border-b border-dashed">
                  <span className="text-muted-foreground">
                    {lang === 'es' ? 'Cierre actual:' : 'Current closing:'}
                  </span>
                  <span className="font-mono font-medium">
                    {formatCurrency(parseFloat(formData.valor_cierre), selectedAccount?.moneda)}
                  </span>
                </div>
                {parseFloat(formData.aportaciones) > 0 && (
                  <div className="flex justify-between items-center py-1 border-b border-dashed">
                    <span className="text-muted-foreground">
                      {lang === 'es' ? 'Aportaciones:' : 'Contributions:'}
                    </span>
                    <span className="font-mono text-blue-600">
                      +{formatCurrency(parseFloat(formData.aportaciones), selectedAccount?.moneda)}
                    </span>
                  </div>
                )}
                {parseFloat(formData.retiros) > 0 && (
                  <div className="flex justify-between items-center py-1 border-b border-dashed">
                    <span className="text-muted-foreground">
                      {lang === 'es' ? 'Retiros:' : 'Withdrawals:'}
                    </span>
                    <span className="font-mono text-rose-600">
                      -{formatCurrency(parseFloat(formData.retiros), selectedAccount?.moneda)}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="border-t-2 pt-3">
                {preview.ganancia !== null ? (
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      {lang === 'es' ? 'Rendimiento neto:' : 'Net profit:'}
                    </span>
                    <div className="text-right">
                      <p className={cn(
                        "text-xl font-bold font-mono",
                        preview.ganancia >= 0 ? "text-emerald-600" : "text-rose-600"
                      )}>
                        {formatCurrencyWithSign(preview.ganancia, selectedAccount?.moneda)}
                      </p>
                      <p className={cn(
                        "text-sm font-medium",
                        preview.porcentaje >= 0 ? "text-emerald-600" : "text-rose-600"
                      )}>
                        {preview.porcentaje >= 0 ? '+' : ''}{preview.porcentaje?.toFixed(2)}%
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center">
                    {lang === 'es' 
                      ? 'Primer registro - rendimiento no calculable'
                      : 'First record - profit not calculable'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit Button */}
        <div className="pt-4 space-y-3">
          <Button
            type="submit"
            disabled={saving || !formData.cuenta_id || !formData.valor_cierre}
            className="w-full h-12 rounded-xl bg-black text-white hover:bg-black/90"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {lang === 'es' ? 'Guardando...' : 'Saving...'}
              </>
            ) : (
              editingLog 
                ? (lang === 'es' ? 'Actualizar registro' : 'Update record')
                : (lang === 'es' ? 'Guardar cierre mensual' : 'Save monthly closing')
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/inversiones')}
            className="w-full h-10"
          >
            {lang === 'es' ? 'Cancelar' : 'Cancel'}
          </Button>
        </div>
      </form>
      </div>
    </div>
  )
}
